// pf.service.js
// Provident Fund (PF) calculation service with backend integration
// Implements EPF Act 1952 compliance with wage ceiling and exemptions

import { api } from './api';

class PFService {
    /**
     * Calculate PF using backend API with automatic fallback
     * @param {string} employeeId - Employee UUID
     * @param {number} basicSalary - Basic salary amount
     * @param {number} daSalary - Dearness Allowance (optional, defaults to 0)
     * @param {string} month - Month in 'YYYY-MM' format (optional, defaults to current month)
     * @returns {Promise<Object>} PF calculation result with source tracking
     */
    async calculatePF(employeeId, basicSalary, daSalary = 0, month = null) {
        try {
            // Prepare request payload
            const payload = {
                employeeId,
                basicSalary: parseFloat(basicSalary) || 0,
                daSalary: parseFloat(daSalary) || 0
            };

            // Add month if provided, otherwise backend uses current month
            if (month) {
                payload.month = month;
            }

            // Call backend PF calculation API
            const response = await api.post('/api/pf/calculate', payload);

            if (response.data.success) {
                const data = response.data.data;

                return {
                    pfAmount: data.pfAmount || 0,
                    pfDetails: data.pfDetails || null,
                    exemptionApplied: data.exemptionApplied || false,
                    exemptionReason: data.exemptionReason || null,
                    source: 'backend'
                };
            }

            // Backend returned unsuccessful response
            throw new Error(response.data.message || 'Backend PF calculation failed');

        } catch (error) {
            console.warn('Backend PF calculation failed, using frontend fallback:', error.message);

            // Fallback to frontend calculation with wage ceiling
            const wageCeiling = 15000; // EPF Act 1952 standard wage ceiling
            const pfWages = Math.min(
                parseFloat(basicSalary || 0) + parseFloat(daSalary || 0),
                wageCeiling
            );
            const pfAmount = Math.round(pfWages * 0.12); // 12% employee contribution

            return {
                pfAmount,
                pfDetails: {
                    basic_salary: parseFloat(basicSalary || 0),
                    da_salary: parseFloat(daSalary || 0),
                    pf_wages: pfWages,
                    employee_pf_contribution: pfAmount,
                    wage_ceiling_applied: (parseFloat(basicSalary || 0) + parseFloat(daSalary || 0)) > wageCeiling,
                    employee_contribution_rate: 0.12
                },
                exemptionApplied: false,
                exemptionReason: null,
                source: 'frontend-fallback'
            };
        }
    }

    /**
     * Synchronous PF calculation with wage ceiling (for proration/projection use)
     * @param {number} basicSalary - Basic salary amount
     * @param {number} daSalary - Dearness Allowance (optional, defaults to 0)
     * @returns {number} Employee PF contribution amount
     */
    calculatePFSync(basicSalary, daSalary = 0) {
        const wageCeiling = 15000; // EPF Act 1952 standard wage ceiling
        const pfWages = Math.min(
            parseFloat(basicSalary || 0) + parseFloat(daSalary || 0),
            wageCeiling
        );
        return Math.round(pfWages * 0.12); // 12% employee contribution
    }

    /**
     * Get current month in YYYY-MM format
     * @returns {string} Current month
     */
    getCurrentMonth() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    }
}

// Export singleton instance
export default new PFService();
